import {
  buildImageGenMetaFromTimelineRound,
} from '@glyphic/shared-core'
import { motion, useReducedMotion } from 'framer-motion'
import type { FormEvent } from 'react'
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  mergeReducedMotionPreset,
  resolveTabIndicatorTransition,
  resolveTransition,
  useThemeMotion,
} from '../animation/themeMotion'
import { absolutePublicUploadUrl, uploadReferenceImageToDisk } from '../api/referenceUpload'
import { PageAsyncErrorState } from '../components/async/PageAsyncErrorState'
import { PlatformConfigLoadingPanel } from '../components/async/PlatformConfigLoadingPanel'
import { ChromePrimaryButton, ChromeToolbarButton, ChromeToolbarDangerButton } from '../components/ChromeActions'
import { ChromeComposerTextarea } from '../components/ChromeComposerTextarea'
import { ChromeDropZoneOverlay } from '../components/ChromeDropZoneOverlay'
import { ChromeRoundActionMenu } from '../components/ChromeRoundActionMenu'
import { chromeTimelineKpiChipShellClass } from '../components/ChromeSurface'
import { ImageGenParamsSection } from '../components/create/ImageGenParamsSection'
import { PromptComposerSection } from '../components/create/PromptComposerSection'
import { PromptTemplatesManageModal } from '../components/create/PromptTemplatesManageModal'
import { TimelineAspectImage } from '../components/create/TimelineAspectImage'
import { TimelineDeferPlaceholder } from '../components/create/TimelineDeferPlaceholder'
import { TimelineListShell, TimelineRoundGapRow } from '../components/create/TimelineListShell'
import { TimelinePullIndicator } from '../components/create/TimelineRefreshSurfaces'
import { TimelineRoundRail } from '../components/create/TimelineRoundRail'
import { TimelineRoundStrip } from '../components/create/TimelineRoundStrip'
import { ThemeModal } from '../components/modal'
import { ThemeReveal, ThemeStagger } from '../components/motion/ThemeMotion'
import {
  SaveToLibraryForm,
  type SaveToLibraryFormHandle,
  type SaveToLibraryModalFooterState,
} from '../components/SaveToLibraryForm'
import { timelineFrameClampStyle, timelineImageAspectStyle, timelineImageMinHeightClass } from '../constants/gptImageApi'
import { useImageLightbox } from '../context/ImageLightboxContext'
import { usePlatformConfig } from '../context/PlatformConfigContext'
import { useTimeline } from '../context/TimelineContext'
import { useToast } from '../context/ToastContext'
import { useImageDropAndPaste } from '../hooks/useImageDropAndPaste'
import { useLibraryCatalog } from '../hooks/useLibraryCatalog'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { usePreferHoverChromeTooltips } from '../hooks/usePreferHoverChromeTooltips'
import { useTimelineRefreshController } from '../hooks/useTimelineRefreshController'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { ContentSkeleton } from '../layouts/ContentSkeleton'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { isReferenceImageUploadSupported } from '../lib/bananaModel'
import {
  effectiveAspectRatios,
  effectiveImageSizes,
  isClientUiImageSelectionValid,
  previewDimensionsFromClientUiModel,
  resolveApiSizeForOptions,
  resolveAspectRatioForOptions,
  resolveClientUiImageModel,
  resolveInitialCatalogPlatformId,
  resolveModelIdForPlatform,
  syncExtraParamValuesForModel,
} from '../lib/clientUiImageModel'
import { persistComposerExpanded, readStoredComposerExpanded } from '../lib/composerPersistence'
import { clearCreatePageDraft, EMPTY_DRAFT, readCreatePageDraft, writeCreatePageDraft } from '../lib/createPageDraft'
import {
  appendCreatePageTemplateInsertion,
  type CreatePageTemplateInsertion,
  removeCreatePageTemplateInsertion,
} from '../lib/createPageTemplateInsertion'
import {
  CREATE_PAGE_GENERATION_DEFER_MS,
  isDeferringTimelineRound,
  isRoundAwaitingGeneration,
  outputSuperResolutionTimelineChip,
  shouldIgnoreTimelinePullStart,
  timelineRoundAccent,
} from '../lib/createPageTimelinePresentation'
import { stampTimelineRoundAt } from '../lib/dateDisplay'
import { persistExtraParams, readStoredExtraParams } from '../lib/extraParamsPersistence'
import { persistImageGenPrefs, readStoredImageGenPrefs } from '../lib/imageGenPrefsPersistence'
import { validateImageGenParams } from '../lib/imageGenValidation'
import {
  REFERENCE_IMAGE_MAX_BYTES,
  REFERENCE_IMAGE_MAX_SIZE_LABEL,
  referenceImageUploadFolderPath,
} from '../lib/libraryPaths'
import { createLogger } from '../lib/logger'
import {
  isPlatformCatalogLoadFailed,
  isPlatformCatalogLoadPending,
} from '../lib/platformConfigLoadState'
import { buildPlatformMenuOptions } from '../lib/platformMenuOptions'
import { fetchPromptTemplates, type PromptTemplate } from '../lib/promptTemplateStore'
import {
  collapsedTimelinePrompt,
  PROMPT_COLLAPSE_THRESHOLD,
  shortenPrompt,
} from '../lib/promptTruncate'
import { randomUuid } from '../lib/randomUuid'
import { validateReferenceImagesForRemoteApi } from '../lib/referenceImageSubmitValidation'
import { convertQiniuReferenceUrlToLocal } from '../lib/referenceImageUrl'
import {
  extraParamValuesFromSnapshot,
  resolveSnapshotDimensionForResubmit,
} from '../lib/roundGenerationSnapshot'
import { timelineExpectedImageCount } from '../lib/timelineImageCount'
import { countRoundItemsInLibrary } from '../lib/timelineLibrary'
import { computeTimelineRailMarkers } from '../lib/timelineRailMarkers'
import { normalizeTimelineRound } from '../lib/timelineRoundNormalize'
import { type TimelineRoundStatus, timelineStemCss } from '../lib/timelineStem'
import { sanitizeUserFacingErrorMessage } from '../lib/userFacingErrorMessage'
import { useTheme } from '../theme/ThemeContext'
import type {
  GenItem,
  ImageGenParams,
  TimelineRound,
} from '../types/timeline'
import { useCreatePageGenerationSubmit } from './useCreatePageGenerationSubmit'

const DEFAULT_REFERENCE_IMAGE_MAX = 8
const createPageLogger = createLogger('create-page')

/** 时间线横滑图卡横向尺寸：在原 min(85vw,22rem) / sm:20rem / lg:22rem 基础上整体 ×0.75（缩小 25%） */
const TIMELINE_STRIP_CARD_WIDTH_CLASS =
  'w-[min(63.75vw,16.5rem)] shrink-0 sm:w-60 lg:w-[16.5rem]'

export function CreatePage() {
  // sessionStorage 草稿恢复：刷新不丢输入，关标签页清空
  const draft = useRef(readCreatePageDraft() ?? EMPTY_DRAFT).current

  const { theme } = useTheme()
  const wideScreen = useMediaQuery('(min-width: 1024px)')
  const preferHoverTitles = usePreferHoverChromeTooltips()
  const prefersReducedMotion = useReducedMotion()
  const motionPreset = useThemeMotion()
  const tabMotionPreset = mergeReducedMotionPreset(motionPreset, !!prefersReducedMotion)
  const tabIndicatorTransition = resolveTabIndicatorTransition(tabMotionPreset, {
    reducedMotion: !!prefersReducedMotion,
  })
  const panelCrossfade = resolveTransition(
    mergeReducedMotionPreset(
      { ...motionPreset, useSpring: false },
      !!prefersReducedMotion,
    ),
  )
  const { showToast } = useToast()
  const canSubmitCreation = useUserPerm(USER_PERMISSION.creationTaskSubmit)
  const {
    clientUi,
    loading: platformConfigLoading,
    remoteSynced: platformConfigRemoteSynced,
    error: platformConfigError,
    refresh: refreshPlatformConfig,
  } = usePlatformConfig()
  /** 首屏仍在拉服务端 catalog：不渲染硬编码下拉，避免与真实列表切换时闪烁 */
  const platformCatalogLoadSnapshot = {
    loading: platformConfigLoading,
    remoteSynced: platformConfigRemoteSynced,
    error: platformConfigError,
  }
  const awaitingFirstPlatformSync = isPlatformCatalogLoadPending(platformCatalogLoadSnapshot)
  const platformCatalogLoadFailed = isPlatformCatalogLoadFailed(platformCatalogLoadSnapshot)
  /** 仅在使用服务端下发的 {@code ClientUiCatalog} 成功后才允许生图（无臆造清单兜底） */
  const platformCatalogReady =
    platformConfigRemoteSynced && clientUi.imageModels.length > 0 && clientUi.imageSizeOptions.length > 0
  const angular = theme === 'pixel' || theme === 'geek'
  // 使用全局时间线状态
  const {
    hydrated: timelineHydrated,
    loading: timelineRemoteLoading,
    rounds,
    addRound,
    upsertRound,
    removeRound,
    refresh: refreshTimeline,
  } = useTimeline()

  /**
   * 追加生成：不新建时间线条目。
   * 提交时在原轮次上挂一个骨架占位（round.pendingItems），
   * 任务成功由 mergeTerminalCreationIntoRound(isAppend) 把新图接到原 items 末尾并移除骨架；
   * 失败 / 取消 / 结果未知一律只丢骨架，绝不留下新的时间线。
   */
  const displayRounds = useMemo(() => {
    if (!timelineHydrated) return []
    return rounds.map((round) => ({
      ...round,
      referenceImages: round.referenceImages?.map(convertQiniuReferenceUrlToLocal),
    }))
  }, [rounds, timelineHydrated])

  const timelineRailMarkers = useMemo(
    () => computeTimelineRailMarkers(displayRounds.map((r) => r.at)),
    [displayRounds],
  )

  const timelineSpineSegmentStops = useMemo(
    () =>
      displayRounds.map((round, roundIdx) => {
        const roundView = normalizeTimelineRound(round)
        const status: TimelineRoundStatus =
          roundView.status ?? (roundView.items.length > 0 ? 'SUCCEEDED' : 'PENDING')
        const from = timelineStemCss(
          isDeferringTimelineRound(roundView),
          isRoundAwaitingGeneration(roundView),
          status,
        )
        const nextRaw = displayRounds[roundIdx + 1]
        if (!nextRaw) return { from, to: from }
        const nextView = normalizeTimelineRound(nextRaw)
        const nextStatus: TimelineRoundStatus =
          nextView.status ?? (nextView.items.length > 0 ? 'SUCCEEDED' : 'PENDING')
        const to = timelineStemCss(
          isDeferringTimelineRound(nextView),
          isRoundAwaitingGeneration(nextView),
          nextStatus,
        )
        return { from, to }
      }),
    [displayRounds],
  )

  const [modelId, setModelId] = useState(draft.modelId)
  const [apiSize, setApiSize] = useState(draft.apiSize)
  // 初始化：有草稿（上次保存）用草稿；没有则先留空，待 catalog 就绪后由同步 effect 回填为模型第一项
  const [aspectRatio, setAspectRatio] = useState(draft.aspectRatio || '')
  const [mobileTab, setMobileTab] = useState<'config' | 'timeline'>('config')
  const [prompt, setPrompt] = useState(draft.prompt)
  const [saveModalItems, setSaveModalItems] = useState<GenItem[]>([])
  /** 重新生成弹窗状态 */
  const [retryModalOpen, setRetryModalOpen] = useState(false)
  const [retryRoundId, setRetryRoundId] = useState<string | null>(null)
  const [retryPrompt, setRetryPrompt] = useState('')
  /** 成功卡片删除二次确认 */
  const [confirmDeleteRoundId, setConfirmDeleteRoundId] = useState<string | null>(null)
  /**
   * 提交成功后清空草稿状态。
   * 以后新增草稿字段只需在这里追加对应的 setter，所有提交成功路径自动同步。
   * 保持 platformId / modelId / extraParamValues / aspectRatio 不变（用户偏好留作下次生成的起点）。
   */
  const resetDraftStateOnSubmitted = useCallback(() => {
    setPrompt('')
    setReferenceAbsoluteUrls([])
    setApiSize('')
    setOutputSuperResolution(false)
    clearCreatePageDraft()
  }, [])

  // ----

  const [extraParamValues, setExtraParamValues] = useState<Record<string, string>>(() => readStoredExtraParams())

  const platformOptions = useMemo(() => buildPlatformMenuOptions(clientUi), [clientUi])

  /** 与 catalog 顺序一致：多平台同 modelId 时取 imageModels 中首条，便于后续加平台而不写死 Duomi/NowCoding 分支 */
  const [platformId, setPlatformId] = useState(draft.platformId)

  /** 本地「平台 + 模型」恢复完成后再写入，避免未校验的 localStorage 污染首帧 */
  const [imageSelectionHydrated, setImageSelectionHydrated] = useState(false)

  const currentModel = useMemo(
    () => resolveClientUiImageModel(clientUi, modelId, platformId),
    [clientUi, modelId, platformId],
  )

  // 当前模型的额外参数选项
  const extraOptions = useMemo(() => currentModel?.extraOptions ?? [], [currentModel])

  const modelMenuOptions = useMemo(() => {
    return clientUi.imageModels
      .filter((m) => m.platform === platformId)
      .map((m) => ({
        value: m.id,
        label: m.label,
        description: m.description || undefined,
      }))
  }, [clientUi.imageModels, platformId])

  const handlePlatformChange = useCallback((newPlatformId: string) => {
    setPlatformId(newPlatformId)
    setModelId('')
  }, [])

  const handleCreateModelChange = useCallback((nextModelId: string) => {
    setModelId(nextModelId)
  }, [])

  /** 处理额外参数变更 */
  const handleExtraValueChange = useCallback((key: string, value: string) => {
    setExtraParamValues((prev) => {
      const next = { ...prev, [key]: value }
      persistExtraParams(next)
      return next
    })
  }, [])
  const [saveModalReferenceUrls, setSaveModalReferenceUrls] = useState<string[] | undefined>(undefined)
  const saveToLibraryFormRef = useRef<SaveToLibraryFormHandle>(null)
  const saveModalOpen = saveModalItems.length > 0
  // 页面加载即拉取图库状态：用于时间线"已在图库"判断 + 存入去重检查
  const libraryCatalogEnabled = true
  const {
    catalog: libraryCatalog,
    loading: libraryCatalogLoading,
    error: libraryCatalogError,
    refresh: refreshLibraryCatalog,
  } = useLibraryCatalog({ enabled: libraryCatalogEnabled })
  const libraryCatalogErrorNotifiedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!libraryCatalogEnabled || !libraryCatalogError) return
    if (libraryCatalogErrorNotifiedRef.current === libraryCatalogError) return
    libraryCatalogErrorNotifiedRef.current = libraryCatalogError
    showApiErrorToast(showToast, new Error(libraryCatalogError), '图库加载失败', 'warning')
  }, [libraryCatalogEnabled, libraryCatalogError, showToast])
  const [saveLibraryFooter, setSaveLibraryFooter] = useState<SaveToLibraryModalFooterState>({
    busy: false,
    primaryLabel: '存入图库',
    primaryDisabled: false,
  })
  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [activeRoundPromptId, setActiveRoundPromptId] = useState<string | null>(null)
  const { open: openImageLightbox } = useImageLightbox()
  /** 提示区快捷模板 chip：仍保持原文、可安全移除的模板 id */
  const [activeTemplateIds, setActiveTemplateIds] = useState<string[]>([])
  const templateInsertionsRef = useRef<CreatePageTemplateInsertion[]>([])
  const isGenerating = rounds.some((r) => isDeferringTimelineRound(r) || isRoundAwaitingGeneration(r))
  const roundsRef = useRef(rounds)
  const generationDeferControllerRef = useRef<AbortController | null>(null)
  const deferringRoundIdRef = useRef<string | null>(null)
  useEffect(() => {
    roundsRef.current = rounds
  }, [rounds])

  const [referenceAbsoluteUrls, setReferenceAbsoluteUrls] = useState<string[]>(
    draft.referenceAbsoluteUrls,
  )
  const [refUploadBusy, setRefUploadBusy] = useState(false)
  const [outputSuperResolution, setOutputSuperResolution] = useState(draft.outputSuperResolution)
  const timelineScrollRef = useRef<HTMLDivElement | null>(null)
  const [timelinePullAttachSignal, setTimelinePullAttachSignal] = useState(0)
  const setTimelineScrollPort = useCallback((node: HTMLDivElement | null) => {
    timelineScrollRef.current = node
    setTimelinePullAttachSignal((n) => n + 1)
  }, [])
  /** 提示词面板展开状态，从 localStorage 读取 */
  const [promptComposerExpanded, setPromptComposerExpanded] = useState(() => readStoredComposerExpanded())

  /** 持久化提示词面板展开状态 */
  const handleToggleComposerExpanded = useCallback(() => {
    setPromptComposerExpanded((v) => {
      const next = !v
      persistComposerExpanded(next)
      return next
    })
  }, [])

  const referenceImagesSupported = useMemo(() => {
    // catalog 还没 load 完：保守假设支持，避免刷新时短暂 false 清空已选参考图
    if (!clientUi.imageModels.length) return true
    return isReferenceImageUploadSupported(platformId, modelId, clientUi.imageModels)
  }, [platformId, modelId, clientUi.imageModels])

  const referenceImageMax = useMemo(() => {
    const max = currentModel?.maxReferenceImages
    return typeof max === 'number' && max > 0 ? max : DEFAULT_REFERENCE_IMAGE_MAX
  }, [currentModel?.maxReferenceImages])

  const {
    containerRef: createFormRef,
    dragActive: imageDragActive,
  } = useImageDropAndPaste({
    onImages: (files) => {
      void handleReferenceFiles(files)
    },
  })

  useEffect(() => {
    if (referenceImagesSupported) return
    setReferenceAbsoluteUrls((prev) => {
      if (prev.length === 0) return prev
      showToast({
        message: '当前平台或模型不支持参考图，已清空已选参考图',
        variant: 'warning',
        autoCloseMs: 4200,
      })
      return []
    })
  }, [referenceImagesSupported, showToast])
  const referenceSuperResolutionAvailable = clientUi.referenceSuperResolutionAvailable === true

  const effectiveSizes = useMemo(
    () => effectiveImageSizes(clientUi, modelId, platformId),
    [clientUi, modelId, platformId],
  )
  const aspectOptions = useMemo(
    () => effectiveAspectRatios(clientUi, modelId, platformId),
    [clientUi, modelId, platformId],
  )

  const sizeMenuOptions = useMemo(
    () =>
      effectiveSizes.map((o) => ({
        value: o.value,
        label: o.label,
        description: o.description,
      })),
    [effectiveSizes],
  )

  const aspectMenuOptions = useMemo(
    () => (aspectOptions ?? []).map((o) => ({ value: o.value, label: o.label })),
    [aspectOptions],
  )

  /** 平台/模型/catalog 变更后同步尺寸、比例与高级选项（避免切换模型后 apiSize 为空） */
  useEffect(() => {
    if (!imageSelectionHydrated) return
    if (!clientUi.imageModels.length) return

    const nextModelId = resolveModelIdForPlatform(clientUi.imageModels, platformId, modelId)
    if (nextModelId !== modelId) {
      setModelId(nextModelId)
      return
    }
    if (!nextModelId) return

    const nextApiSize = resolveApiSizeForOptions(effectiveSizes, apiSize)
    if (nextApiSize !== apiSize) setApiSize(nextApiSize)

    // 保留用户已选比例：选项集合变化（catalog 刷新/模型切换）时不静默跳回横屏；
    // 当前为空（无上次保存）时回填模型比例选项的第一项
    const nextAspect = resolveAspectRatioForOptions(aspectOptions, aspectRatio, '', true)
    if (nextAspect !== aspectRatio) setAspectRatio(nextAspect)

    const model = resolveClientUiImageModel(clientUi, nextModelId, platformId)
    setExtraParamValues((prev) => {
      const next = syncExtraParamValuesForModel(model, prev)
      const unchanged =
        Object.keys(next).length === Object.keys(prev).length &&
        Object.entries(next).every(([k, v]) => prev[k] === v)
      if (unchanged) return prev
      persistExtraParams(next)
      return next
    })
  }, [
    imageSelectionHydrated,
    clientUi,
    platformId,
    modelId,
    effectiveSizes,
    aspectOptions,
    apiSize,
    aspectRatio,
  ])

  const {
    pullPx: timelinePullPx,
    commitFlying: timelinePullCommitFlying,
    onCommitFlyComplete: onTimelinePullCommitFlyComplete,
    releaseThreshold: timelinePullReleaseThreshold,
    refreshing: timelineRefreshing,
    indicatorRefreshing: timelineIndicatorRefreshing,
  } = useTimelineRefreshController({
    scrollRef: timelineScrollRef,
    attachSignal: timelinePullAttachSignal,
    enabled: wideScreen || mobileTab === 'timeline',
    hydrated: timelineHydrated,
    remoteLoading: timelineRemoteLoading,
    refreshTimeline,
    shouldIgnorePullStart: shouldIgnoreTimelinePullStart,
    showToast,
  })

  useEffect(() => {
    if (imageSelectionHydrated) return
    if (awaitingFirstPlatformSync) return
    if (!clientUi.imageModels.length) return

    // 优先级：sessionStorage 草稿 > localStorage 用户偏好 > catalog 默认
    const draft = readCreatePageDraft()
    if (draft && draft.modelId && draft.platformId && isClientUiImageSelectionValid(clientUi, draft.modelId, draft.platformId)) {
      setPlatformId(draft.platformId)
      setModelId(draft.modelId)
    } else {
      const stored = readStoredImageGenPrefs()
      if (stored && isClientUiImageSelectionValid(clientUi, stored.modelId, stored.platformId)) {
        setPlatformId(stored.platformId)
        setModelId(stored.modelId)
      } else {
        setPlatformId(resolveInitialCatalogPlatformId(clientUi))
      }
    }
    setImageSelectionHydrated(true)
  }, [clientUi, imageSelectionHydrated, awaitingFirstPlatformSync])

  // 自动保存草稿到 sessionStorage（debounce 300ms）
  useEffect(() => {
    const t = setTimeout(() => {
      writeCreatePageDraft({
        prompt,
        platformId,
        modelId,
        apiSize,
        aspectRatio,
        outputSuperResolution,
        referenceAbsoluteUrls,
      })
    }, 300)
    return () => clearTimeout(t)
  }, [prompt, platformId, modelId, apiSize, aspectRatio, outputSuperResolution, referenceAbsoluteUrls])

  useEffect(() => {
    if (!imageSelectionHydrated) return
    persistImageGenPrefs({ platformId, modelId })
  }, [imageSelectionHydrated, platformId, modelId])

  useEffect(() => {
    const resolved = resolveClientUiImageModel(clientUi, modelId, platformId)
    if (!resolved) return
    if (resolved.platform !== platformId) {
      setPlatformId(resolved.platform)
    }
  }, [modelId, platformId, clientUi])

  const { submitRoundWithDefer } = useCreatePageGenerationSubmit({
    platformId,
    modelId,
    apiSize,
    aspectRatio,
    extraParamValues,
    outputSuperResolution,
    referenceSuperResolutionAvailable,
    clientUi,
    upsertRound,
    showToast,
    deferringRoundIdRef,
    generationDeferControllerRef,
  })

  const warnIfReferenceImagesInvalid = useCallback(
    (refSnapshot: readonly string[]) => {
      if (!refSnapshot.length) return false
      const check = validateReferenceImagesForRemoteApi(refSnapshot)
      if (check.ok) return false
      showToast({ message: check.message, variant: 'error', autoCloseMs: 8000 })
      return true
    },
    [showToast],
  )



  /**
   * 追加生成：以已成功轮次的提示词和参数为基线，再提交一次生图请求。
   * 不新建时间线节点——骨架挂在原轮次 pendingItems 上，
   * 成功后 mergeTerminalCreationIntoRound(isAppend) 把新图接到原 items 末尾。
   */
  const appendRoundGeneration = useCallback(
    async (roundId: string) => {
      if (!platformCatalogReady) {
        showToast({ message: '模型配置未就绪，请先完成加载或点击「重新加载」', variant: 'error' })
        return
      }
      if (!canSubmitCreation) {
        showToast({ message: '暂无生图权限', variant: 'warning' })
        return
      }
      const r = roundsRef.current.find((x) => x.id === roundId)
      if (!r) return
      if (r.status !== 'SUCCEEDED' || !r.items.length) {
        showToast({ message: '仅在成功轮次上可以追加生成', variant: 'warning' })
        return
      }
      if ((r.pendingItems?.length ?? 0) > 0) {
        showToast({ message: '本轮已有追加生成在进行中', variant: 'warning' })
        return
      }

      const baseModelId = r.modelId ?? modelId
      const basePlatformId = r.platform ?? platformId
      const baseApiSize = r.params?.size ?? apiSize
      const baseModelCfg = resolveClientUiImageModel(clientUi, baseModelId, basePlatformId)
      const selectionCheck = validateImageGenParams(basePlatformId, baseModelId, baseApiSize, baseModelCfg?.dimensionMode)
      if (!selectionCheck.ok) {
        showToast({ message: selectionCheck.message, variant: 'error' })
        return
      }

      const modelDimensionMode = baseModelCfg?.dimensionMode ?? 'size'
      const baseEffectiveSizes = effectiveImageSizes(clientUi, baseModelId, basePlatformId)
      const baseAspectOptions = effectiveAspectRatios(clientUi, baseModelId, basePlatformId)
      // 画幅参数必须能从「本轮快照」还原：模型定义可能在两次生成之间被调整
      // （如 QIQI GPT-Image-2 从 size 模式改为 aspect_ratio 模式），此时上游会静默丢弃
      // 已不发送的画幅参数、按默认画幅出图（界面标 16:9、实际出 9:16）。
      // 推导不出时显式失败，绝不发空值、绝不静默跑偏。
      const dimensionResolution = resolveSnapshotDimensionForResubmit({
        snapshot: r.params,
        modelDimensionMode,
        aspectOptions: baseAspectOptions,
        sizeOptions: baseEffectiveSizes.length > 0 ? baseEffectiveSizes : undefined,
      })
      if (!dimensionResolution.ok) {
        showToast({ message: dimensionResolution.message, variant: 'error' })
        return
      }
      const baseDimensionMode = dimensionResolution.dimensionMode
      const requestSize = dimensionResolution.sizeValue || baseApiSize
      const requestAspectRatio = dimensionResolution.aspectRatio
      const sizeLabel =
        baseDimensionMode === 'aspect_ratio'
          ? (baseAspectOptions?.find((o) => o.value === requestAspectRatio)?.label ?? requestAspectRatio)
          : (baseEffectiveSizes.find((o) => o.value === requestSize)?.label ?? requestSize)
      const label = r.modelLabel ?? baseModelId

      const refSnap = r.referenceImages?.length ? [...r.referenceImages] : []
      if (warnIfReferenceImagesInvalid(refSnap)) return

      const text = r.prompt
      // 追加固定一张：只有一个骨架占位
      const appendCount = 1
      const skeletonId = crypto.randomUUID()
      // 骨架挂到原轮次上，原图与成功态都保留；空闲骨架数量即正在生成的张数。
      // 注意保留原轮次 params：追加用的是本次 capture 快照，不能改写该轮「张数」等元信息。
      const roundWithSkeleton: TimelineRound = {
        ...r,
        pendingItems: [
          ...(r.pendingItems ?? []),
          { id: skeletonId },
        ],
        pendingSubmitDeadline: Date.now() + CREATE_PAGE_GENERATION_DEFER_MS,
      }

      upsertRound(roundWithSkeleton)
      setMobileTab('timeline')

      await submitRoundWithDefer(
        roundWithSkeleton,
        {
          text,
          refSnapshot: refSnap,
          label,
          sizeLabel,
          count: appendCount,
          // 关键：沿用本轮快照的画幅与参数，不能回落到表单当前值
          aspectRatio: baseDimensionMode === 'aspect_ratio' ? requestAspectRatio : '',
          dimensionMode: baseDimensionMode,
          sizeValue: baseDimensionMode === 'size' ? requestSize : '',
          extraParamValues: extraParamValuesFromSnapshot(r.params),
        },
        { skeletonId },
      )
    },
    [
      platformCatalogReady,
      canSubmitCreation,
      modelId,
      platformId,
      apiSize,
      clientUi,
      upsertRound,
      submitRoundWithDefer,
      warnIfReferenceImagesInvalid,
      showToast,
    ],
  )

  const confirmRetryGeneration = useCallback(
    async (text: string) => {
      if (!retryRoundId) return
      const r = roundsRef.current.find((x) => x.id === retryRoundId)
      if (!r) return
      const retryModelId = r.modelId ?? modelId
      const retryPlatformId = r.platform ?? platformId
      const retryApiSize = r.params?.size ?? apiSize
      const retryModelCfg = resolveClientUiImageModel(clientUi, retryModelId, retryPlatformId)
      const retrySelectionCheck = validateImageGenParams(retryPlatformId, retryModelId, retryApiSize, retryModelCfg?.dimensionMode)
      if (!retrySelectionCheck.ok) {
        showToast({ message: retrySelectionCheck.message, variant: 'error' })
        return
      }
      const retryEffectiveSizes = effectiveImageSizes(clientUi, retryModelId, retryPlatformId)
      const retryAspectOptions = effectiveAspectRatios(clientUi, retryModelId, retryPlatformId)
      // 与追加一致：画幅参数必须能从「本轮快照」还原（模型定义可能已切换画幅模式），
      // 推导不出时显式失败，绝不发空值让上游按默认画幅出图
      const retryDimension = resolveSnapshotDimensionForResubmit({
        snapshot: r.params,
        modelDimensionMode: retryModelCfg?.dimensionMode ?? 'size',
        aspectOptions: retryAspectOptions,
        sizeOptions: retryEffectiveSizes.length > 0 ? retryEffectiveSizes : undefined,
      })
      if (!retryDimension.ok) {
        showToast({ message: retryDimension.message, variant: 'error' })
        return
      }
      setRetryModalOpen(false)
      const refSnap =
        referenceImagesSupported && referenceAbsoluteUrls.length > 0
          ? [...referenceAbsoluteUrls]
          : []
      if (warnIfReferenceImagesInvalid(refSnap)) return
      const label = currentModel?.label ?? retryModelId
      const retryDimensionMode = retryDimension.dimensionMode
      const retryRequestSize = retryDimension.sizeValue || retryApiSize
      const retryRequestAspect = retryDimension.aspectRatio
      const sizeLabel = retryDimensionMode === 'aspect_ratio'
        ? (retryAspectOptions?.find((o) => o.value === retryRequestAspect)?.label ?? retryRequestAspect)
        : (retryEffectiveSizes.find((o) => o.value === retryRequestSize)?.label ?? retryRequestSize)

      // width/height：优先沿用旧 snapshot 的宽高；缺失时按画幅参数推算（给 meta display 兜底）
      let retryW = 0, retryH = 0
      if (r.params?.width != null && r.params.height != null) {
        retryW = r.params.width; retryH = r.params.height
      }
      if (!(retryW > 0 && retryH > 0)) {
        if (retryDimensionMode === 'aspect_ratio' && retryRequestAspect) {
          const m = retryRequestAspect.match(/^(\d+)\s*[:：]\s*(\d+)$/)
          if (m) {
            const aw = parseInt(m[1]!, 10)
            const ah = parseInt(m[2]!, 10)
            if (aw > 0 && ah > 0) {
              if (aw >= ah) { retryW = 1024; retryH = Math.round(1024 * ah / aw) }
              else { retryH = 1024; retryW = Math.round(1024 * aw / ah) }
            }
          }
        } else if (retryDimensionMode === 'size' && retryEffectiveSizes.length > 0) {
          const dims = previewDimensionsFromClientUiModel(clientUi, retryModelId, retryRequestSize, retryPlatformId)
          retryW = dims.w; retryH = dims.h
        }
      }
      if (!(retryW > 0 && retryH > 0)) { retryW = 1024; retryH = 1024 }

      // 展示快照与提交共用同一份白名单解析（含 output_compression / resolution / input_fidelity）
      const retryExtraFromSnapshot = extraParamValuesFromSnapshot(r.params)
      const retrySnapshotExtra: Record<string, unknown> = {}
      for (const [key, val] of Object.entries(retryExtraFromSnapshot)) {
        if (key === 'n') continue
        if (val) retrySnapshotExtra[key] = val
      }
      if (retryDimensionMode === 'aspect_ratio' && retryRequestAspect) {
        retrySnapshotExtra.aspect_ratio = retryRequestAspect
      }
      // n 优先用旧值，兜底 1
      const retryN = (typeof r.params?.n === 'number' && r.params.n >= 1) ? r.params.n : 1

      const paramsSnapshot: ImageGenParams = {
        size: retryDimensionMode === 'aspect_ratio' ? '' : retryRequestSize,
        width: retryW,
        height: retryH,
        n: retryN,
        ...retrySnapshotExtra,
      }
      // 先移除原失败/取消节点，再生成全新节点
      removeRound(retryRoundId)
      const at = stampTimelineRoundAt()
      const newRoundId = crypto.randomUUID()
      const placeholderRound: TimelineRound = {
        id: newRoundId,
        at,
        platform: retryPlatformId,
        modelId: retryModelId,
        modelLabel: label,
        aspectLabel: sizeLabel,
        params: paramsSnapshot,
        prompt: text,
        promptPreview: shortenPrompt(text),
        status: 'PENDING',
        items: [],
        taskId: undefined,
        errorMessage: undefined,
        outputSuperResolution: undefined,
        pendingSubmitDeadline: Date.now() + CREATE_PAGE_GENERATION_DEFER_MS,
        ...(refSnap.length ? { referenceImageCount: refSnap.length, referenceImages: refSnap } : {}),
      }
      upsertRound(placeholderRound)
      setActiveRoundPromptId(newRoundId)
      setMobileTab('timeline')
      await submitRoundWithDefer(placeholderRound, {
        text,
        refSnapshot: refSnap,
        label,
        sizeLabel,
        count: timelineExpectedImageCount({ ...placeholderRound, params: paramsSnapshot }),
        // 重新生成同样沿用旧轮次快照，避免「显示 16:9、实际按 auto/默认画幅出图」
        aspectRatio: retryDimensionMode === 'aspect_ratio' ? retryRequestAspect : '',
        dimensionMode: retryDimensionMode,
        sizeValue: retryDimensionMode === 'size' ? retryRequestSize : '',
        extraParamValues: extraParamValuesFromSnapshot(r.params),
      })
      setRetryRoundId(null)
      setRetryPrompt('')
    },
    [
      retryRoundId,
      removeRound,
      referenceImagesSupported,
      referenceAbsoluteUrls,
      warnIfReferenceImagesInvalid,
      currentModel?.label,
      modelId,
      apiSize,
      clientUi,
      platformId,
      upsertRound,
      submitRoundWithDefer,
      showToast,
    ],
  )

  const refreshTemplates = useCallback(async () => {
    try {
      setTemplates(await fetchPromptTemplates())
    } catch {
      /* api 层已提示 */
    }
  }, [])

  useEffect(() => {
    void refreshTemplates()
  }, [refreshTemplates])

  useEffect(() => {
    const valid = new Set(templates.map((t) => t.id))
    templateInsertionsRef.current = templateInsertionsRef.current.filter((item) => valid.has(item.id))
    setActiveTemplateIds((prev) => prev.filter((id) => valid.has(id)))
  }, [templates])

  /**
   * 提示词被清空（提交成功、手动清空）时同步重置模板选中态。
   * 之前只在 handlePromptChange 里同步插入区间，resetDraftStateOnSubmitted 直接 setPrompt('')
   * 绕过该路径，导致模板正文已从输入框消失、chip 仍保持高亮。
   */
  useEffect(() => {
    if (prompt.trim() !== '') return
    if (templateInsertionsRef.current.length > 0) templateInsertionsRef.current = []
    setActiveTemplateIds((prev) => (prev.length === 0 ? prev : []))
  }, [prompt])

  const syncTemplateInsertions = (nextPrompt: string) => {
    const nextInsertions = templateInsertionsRef.current.filter(
      (item) => nextPrompt.slice(item.start, item.end) === item.body,
    )
    if (nextInsertions.length !== templateInsertionsRef.current.length) {
      templateInsertionsRef.current = nextInsertions
      setActiveTemplateIds(nextInsertions.map((item) => item.id))
    }
  }

  const handlePromptChange = (nextPrompt: string) => {
    setPrompt(nextPrompt)
    syncTemplateInsertions(nextPrompt)
  }

  const appendTemplatesByOrderedIds = (orderedIds: string[]) => {
    const records = [...templateInsertionsRef.current]
    const existing = new Set(records.map((item) => item.id))
    let nextPrompt = prompt
    const appendedIds: string[] = []

    for (const id of orderedIds) {
      if (existing.has(id)) continue
      const body = templates.find((t) => t.id === id)?.body
      if (!body) continue
      const appended = appendCreatePageTemplateInsertion(nextPrompt, id, body)
      if (!appended) continue
      nextPrompt = appended.text
      records.push(appended.insertion)
      existing.add(id)
      appendedIds.push(id)
    }

    if (appendedIds.length === 0) return
    templateInsertionsRef.current = records
    setPrompt(nextPrompt)
    setActiveTemplateIds((prev) => [...new Set([...prev, ...appendedIds])])
  }

  const toggleTemplateChip = (t: PromptTemplate) => {
    const id = t.id
    const body = t.body.trim()
    if (!body) return
    if (activeTemplateIds.includes(id)) {
      const insertion = templateInsertionsRef.current.find((item) => item.id === id)
      const removed = insertion ? removeCreatePageTemplateInsertion(prompt, insertion) : null
      if (removed) {
        const delta = removed.removedEnd - removed.removedStart
        templateInsertionsRef.current = templateInsertionsRef.current
          .filter((item) => item.id !== id)
          .map((item) =>
            item.start >= removed.removedEnd
              ? { ...item, start: item.start - delta, end: item.end - delta }
              : item,
          )
        setPrompt(removed.text)
      } else {
        templateInsertionsRef.current = templateInsertionsRef.current.filter((item) => item.id !== id)
      }
      setActiveTemplateIds((prev) => prev.filter((x) => x !== id))
    } else {
      const appended = appendCreatePageTemplateInsertion(prompt, id, body)
      if (!appended) return
      templateInsertionsRef.current = [...templateInsertionsRef.current, appended.insertion]
      setPrompt(appended.text)
      setActiveTemplateIds((prev) => [...prev, id])
    }
  }

  useEffect(() => {
    if (!referenceImagesSupported) {
      setReferenceAbsoluteUrls([])
    }
  }, [referenceImagesSupported])

  useEffect(() => {
    if (!referenceSuperResolutionAvailable) setOutputSuperResolution(false)
  }, [referenceSuperResolutionAvailable])

  const removeReferenceAt = (index: number) => {
    setReferenceAbsoluteUrls((prev) => prev.filter((_, i) => i !== index))
  }

  const handleReferenceFiles = async (files: FileList | readonly File[] | null) => {
    if (!canSubmitCreation) return
    if (!files?.length || !referenceImagesSupported || refUploadBusy) return
    const remaining = referenceImageMax - referenceAbsoluteUrls.length
    if (remaining <= 0) {
      showToast({
        message: `最多 ${referenceImageMax} 张参考图`,
        variant: 'error',
      })
      return
    }
    const list = Array.from(files).slice(0, remaining)
    setRefUploadBusy(true)
    try {
      const next: string[] = []
      const failed: string[] = []
      for (const file of list) {
        if (file.size > REFERENCE_IMAGE_MAX_BYTES) {
          failed.push(`${file.name.trim() || '未命名图片'}（超过 ${REFERENCE_IMAGE_MAX_SIZE_LABEL}）`)
          continue
        }
        try {
          const res = await uploadReferenceImageToDisk(file, referenceImageUploadFolderPath(file))
          next.push(absolutePublicUploadUrl(res.url))
        } catch (error) {
          const label = file.name.trim() || '未命名图片'
          failed.push(label)
          createPageLogger.warn('参考图上传失败', label, error)
        }
      }
      if (next.length > 0) {
        setReferenceAbsoluteUrls((prev) => [...prev, ...next])
      }
      // 一次上传只出一条结论：部分成功也只报一条，避免成功/失败双弹
      const failedBrief = `${failed.slice(0, 2).join('、')}${failed.length > 2 ? '…' : ''}`
      if (failed.length > 0) {
        showToast({
          message:
            next.length > 0
              ? `已上传 ${next.length} 张，${failed.length} 张失败：${failedBrief}`
              : `参考图上传失败：${failedBrief}`,
          variant: next.length > 0 ? 'warning' : 'error',
          autoCloseMs: next.length > 0 ? 6000 : 0,
        })
      } else if (next.length > 0) {
        showToast({
          message: next.length === 1 ? '参考图已上传' : `已上传 ${next.length} 张参考图`,
          variant: 'success',
          autoCloseMs: 2400,
        })
      }
    } finally {
      setRefUploadBusy(false)
    }
  }

  /** 图库选图：直接存入 state（CDN 签名 URL），延迟到提交生图时再转存本地 */
  const handleReferenceLibraryUrls = (urls: readonly string[]) => {
    if (!referenceImagesSupported || urls.length === 0) return
    const normalized = urls
      .map((u) => absolutePublicUploadUrl(u))
      .map((u) => u.trim())
      .filter(Boolean)
    if (!normalized.length) return
    setReferenceAbsoluteUrls((prev) => {
      const next = [...prev]
      const seen = new Set(prev)
      for (const u of normalized) {
        if (next.length >= referenceImageMax) break
        if (seen.has(u)) continue
        seen.add(u)
        next.push(u)
      }
      return next
    })
    showToast({
      message: normalized.length === 1 ? '参考图已添加' : `已添加 ${normalized.length} 张参考图`,
      variant: 'success',
      autoCloseMs: 2400,
    })
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmitCreation) {
      showToast({ message: '没有创作提交权限', variant: 'warning' })
      return
    }
    if (!platformCatalogReady) {
      showToast({ message: '模型配置未就绪，请先完成加载或点击「重新加载」', variant: 'error' })
      return
    }
    const selectionCheck = validateImageGenParams(platformId, modelId, apiSize, currentModel?.dimensionMode)
    if (!selectionCheck.ok) {
      showToast({ message: selectionCheck.message, variant: 'error' })
      return
    }
    const text = prompt.trim()
    if (!text) return
    const label = currentModel?.label ?? modelId
    const count = parseInt(extraParamValues.n ?? '1', 10) || 1
    const dimensionMode = currentModel?.dimensionMode ?? 'size'
    const isAspectMode = dimensionMode === 'aspect_ratio'
    const aspectIsSet = aspectRatio && aspectRatio !== 'auto'

    // width/height 总是存（给 meta display 兜底），来源按 dimensionMode
    let w = 1024, h = 1024
    if (isAspectMode && aspectIsSet) {
      const m = aspectRatio.trim().match(/^(\d+)\s*[:：]\s*(\d+)$/)
      if (m) {
        const aw = parseInt(m[1]!, 10)
        const ah = parseInt(m[2]!, 10)
        if (aw > 0 && ah > 0) {
          if (aw >= ah) { w = 1024; h = Math.round(1024 * ah / aw) }
          else { h = 1024; w = Math.round(1024 * aw / ah) }
        }
      }
    } else if (effectiveSizes.length > 0) {
      const dims = previewDimensionsFromClientUiModel(clientUi, modelId, apiSize, platformId)
      w = dims.w; h = dims.h
    }

    const sizeLabel = isAspectMode
      ? (aspectOptions?.find((o) => o.value === aspectRatio)?.label ?? aspectRatio ?? '')
      : (effectiveSizes.find((o) => o.value === apiSize)?.label ?? apiSize)

    // 画幅参数：二选一存 — dimensionMode=aspect_ratio 存 aspect_ratio，否则存 size
    const snapshotExtra: Record<string, unknown> = {}
    if (isAspectMode && aspectIsSet) {
      snapshotExtra.aspect_ratio = aspectRatio
    }
    for (const [key, val] of Object.entries(extraParamValues)) {
      if (key === 'n') continue
      if (val && val !== 'auto') snapshotExtra[key] = val
    }

    const paramsSnapshot: ImageGenParams = {
      size: isAspectMode ? '' : apiSize,
      width: w,
      height: h,
      n: count,
      ...snapshotExtra,
    }
    const roundId = randomUuid()
    const at = stampTimelineRoundAt()
    const refSnapshot =
      referenceImagesSupported && referenceAbsoluteUrls.length > 0 ? [...referenceAbsoluteUrls] : []
    if (warnIfReferenceImagesInvalid(refSnapshot)) return
    const placeholderRound: TimelineRound = {
      id: roundId,
      platform: platformId,
      prompt: text,
      promptPreview: shortenPrompt(text),
      at,
      modelId,
      modelLabel: label,
      aspectLabel: sizeLabel,
      params: paramsSnapshot,
      status: 'PENDING',
      items: [],
      pendingSubmitDeadline: Date.now() + CREATE_PAGE_GENERATION_DEFER_MS,
      ...(refSnapshot.length ? { referenceImageCount: refSnapshot.length, referenceImages: refSnapshot } : {}),
    }
    addRound(placeholderRound)
    setActiveRoundPromptId(roundId)
    setMobileTab('timeline')

    // 草稿已提交，清空所有输入字段（sessionStorage + React state）
    resetDraftStateOnSubmitted()

    await submitRoundWithDefer(placeholderRound, {
      text,
      refSnapshot,
      label,
      sizeLabel,
      count,
    })
  }

  return (
    <>
    <PromptTemplatesManageModal
      open={templateModalOpen}
      onClose={() => setTemplateModalOpen(false)}
      angular={angular}
      templates={templates}
      onRefresh={refreshTemplates}
      onAppendSelectedTemplateIds={appendTemplatesByOrderedIds}
    />
    <ThemeModal
      open={retryModalOpen}
      onClose={() => {
        setRetryModalOpen(false)
        setRetryRoundId(null)
        setRetryPrompt('')
      }}
      title="重新生成"
      angular={angular}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <ChromeToolbarButton
            type="button"
            onClick={() => {
              setRetryModalOpen(false)
              setRetryRoundId(null)
              setRetryPrompt('')
            }}
          >
            取消
          </ChromeToolbarButton>
          <ChromePrimaryButton
            type="button"
            density="compact"
            disabled={!retryPrompt.trim()}
            onClick={() => void confirmRetryGeneration(retryPrompt.trim())}
          >
            确认生成
          </ChromePrimaryButton>
        </div>
      }
    >
      <ChromeComposerTextarea
        angular={angular}
        variant="form"
        value={retryPrompt}
        onChange={(e) => setRetryPrompt(e.target.value)}
        placeholder="输入提示词…"
        className="!mt-0"
        textareaClassName="min-h-[7.5rem]"
      />
    </ThemeModal>
    <ThemeModal
      open={saveModalOpen}
      onClose={() => {
        setSaveModalItems([])
        setSaveModalReferenceUrls(undefined)
        setSaveLibraryFooter({ busy: false, primaryLabel: '存入图库', primaryDisabled: false })
      }}
      title="存入云端图库"
      angular={angular}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <ChromeToolbarButton
            type="button"
            disabled={saveLibraryFooter.busy}
            onClick={() => {
              setSaveModalItems([])
              setSaveModalReferenceUrls(undefined)
            }}
          >
            取消
          </ChromeToolbarButton>
          <ChromePrimaryButton
            type="button"
            density="compact"
            disabled={saveLibraryFooter.busy || saveLibraryFooter.primaryDisabled}
            onClick={() => saveToLibraryFormRef.current?.submit()}
          >
            {saveLibraryFooter.primaryLabel}
          </ChromePrimaryButton>
        </div>
      }
    >
      {saveModalItems.length > 0 ? (
        <SaveToLibraryForm
          ref={saveToLibraryFormRef}
          key={saveModalItems.map((i) => i.id).join('-')}
          items={saveModalItems}
          referenceImageUrls={saveModalReferenceUrls}
          selectable
          angular={angular}
          onSaved={() => {
            setSaveModalItems([])
            setSaveModalReferenceUrls(undefined)
            setSaveLibraryFooter({ busy: false, primaryLabel: '存入图库', primaryDisabled: false })
          }}
          onModalFooterState={setSaveLibraryFooter}
          libraryCatalog={libraryCatalog}
          libraryCatalogLoading={libraryCatalogLoading}
          onRefreshLibraryCatalog={refreshLibraryCatalog}
        />
      ) : null}
    </ThemeModal>
    <ThemeModal
      open={confirmDeleteRoundId != null}
      onClose={() => setConfirmDeleteRoundId(null)}
      title="确认删除？"
      angular={angular}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <ChromeToolbarButton
            type="button"
            onClick={() => setConfirmDeleteRoundId(null)}
          >
            取消
          </ChromeToolbarButton>
          <ChromeToolbarDangerButton
            type="button"
            onClick={() => {
              if (confirmDeleteRoundId != null) {
                removeRound(confirmDeleteRoundId)
                if (activeRoundPromptId === confirmDeleteRoundId) setActiveRoundPromptId(null)
              }
              setConfirmDeleteRoundId(null)
            }}
          >
            确认删除
          </ChromeToolbarDangerButton>
        </div>
      }
    >
      <p className="text-sm leading-relaxed text-ink">
        从时间线移除本轮（含已生成的图片预览）
      </p>
    </ThemeModal>
    <ContentSkeleton
      title="生图"
      breadcrumbs={[
        { label: '首页', to: '/' },
        { label: '生图' },
      ]}
      adminLayout
      framed={false}
      scrollableBody={false}
      shellScrollable={false}
    >
    <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-hidden lg:flex-row">
      <div className="relative flex shrink-0 bg-panel lg:hidden">
        <motion.div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 z-0 h-[3px] w-1/2 rounded-t-sm bg-accent"
          initial={false}
          animate={{ x: mobileTab === 'timeline' ? '100%' : '0%' }}
          transition={tabIndicatorTransition}
        />
        <button
          type="button"
          onClick={() => setMobileTab('config')}
          className={[
            'relative z-[1] min-h-11 flex-1 text-sm font-semibold transition-colors',
            mobileTab === 'config' ? 'text-ink' : 'text-ink-soft hover:text-ink',
          ].join(' ')}
        >
          参数
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('timeline')}
          className={[
            'relative z-[1] min-h-11 flex-1 text-sm font-semibold transition-colors',
            mobileTab === 'timeline' ? 'text-ink' : 'text-ink-soft hover:text-ink',
          ].join(' ')}
        >
          时间线
        </button>
      </div>

      <div className="relative grid min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-1 overflow-hidden lg:flex lg:min-h-0 lg:min-w-0 lg:flex-row">
      <motion.section
        className={[
          'col-start-1 row-start-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-panel',
          'lg:col-auto lg:row-auto lg:h-full lg:max-h-full lg:w-[min(100%,440px)] lg:shrink-0 lg:flex-none lg:border-b-0',
          'lg:border-r lg:border-[var(--shell-rail-border)] lg:bg-[var(--shell-rail-bg)]',
        ].join(' ')}
        animate={{
          opacity: wideScreen || mobileTab === 'config' ? 1 : 0,
        }}
        transition={panelCrossfade}
        style={{
          pointerEvents: wideScreen || mobileTab === 'config' ? 'auto' : 'none',
          zIndex: wideScreen ? undefined : mobileTab === 'config' ? 2 : 1,
        }}
      >
        <form
          ref={createFormRef}
          onSubmit={onSubmit}
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          <ChromeDropZoneOverlay
            active={imageDragActive}
            message="松开鼠标以上传参考图"
          />
          <div className="scroll-region create-params-scroll min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden ps-4 pe-4 py-3 sm:ps-5 sm:pe-6 sm:py-4 lg:pe-5">
            {awaitingFirstPlatformSync ? (
              <PlatformConfigLoadingPanel angular={angular} />
            ) : platformCatalogLoadFailed ? (
              <PageAsyncErrorState
                theme={theme}
                title="未能加载模型配置"
                detail={
                  platformConfigError ??
                  '请确认后端已启动且可访问「平台配置」接口，然后重试。'
                }
                onRetry={() => void refreshPlatformConfig()}
              />
            ) : (
              <ImageGenParamsSection
                angular={angular}
                platformOptions={platformOptions}
                platformId={platformId}
                onPlatformChange={handlePlatformChange}
                modelMenuOptions={modelMenuOptions}
                modelId={modelId}
                onModelChange={handleCreateModelChange}
                aspectMenuOptions={aspectOptions?.length ? aspectMenuOptions : null}
                aspectRatio={aspectRatio}
                onAspectRatioChange={setAspectRatio}
                apiSize={apiSize}
                onApiSizeChange={setApiSize}
                sizeMenuOptions={sizeMenuOptions}
                dimensionMode={currentModel?.dimensionMode ?? 'size'}
                extraOptions={extraOptions}
                extraValues={extraParamValues}
                onExtraValueChange={handleExtraValueChange}
              />
            )}
          </div>

          <PromptComposerSection
            angular={angular}
            expanded={promptComposerExpanded}
            onToggleExpanded={handleToggleComposerExpanded}
            prompt={prompt}
            onPromptChange={handlePromptChange}
            templates={templates}
            activeTemplateIds={activeTemplateIds}
            onToggleTemplateChip={toggleTemplateChip}
            onOpenTemplateModal={() => {
              if (canSubmitCreation) setTemplateModalOpen(true)
            }}
            isGenerating={isGenerating}
            platformCatalogReady={platformCatalogReady}
            canSubmit={canSubmitCreation}
            timelineHydrated={timelineHydrated}
            referenceImagesSupported={referenceImagesSupported}
            referenceUploadBusy={refUploadBusy}
            referenceAbsoluteUrls={referenceAbsoluteUrls}
            referenceCount={referenceAbsoluteUrls.length}
            referenceMax={referenceImageMax}
            onReferenceFiles={handleReferenceFiles}
            onReferenceLibraryUrls={handleReferenceLibraryUrls}
            onRemoveReference={removeReferenceAt}
            libraryCatalog={libraryCatalog}
            libraryCatalogLoading={libraryCatalogLoading}
            referenceSuperResolutionAvailable={referenceSuperResolutionAvailable}
            outputSuperResolution={outputSuperResolution}
            onOutputSuperResolutionChange={setOutputSuperResolution}
            onRefreshLibraryCatalog={refreshLibraryCatalog}
          />
        </form>
      </motion.section>

      <motion.section
        className={[
          'relative col-start-1 row-start-1 flex min-h-0 min-w-0 flex-1 flex-col bg-surface',
          'lg:col-auto lg:row-auto lg:min-w-0',
        ].join(' ')}
        animate={{
          opacity: wideScreen || mobileTab === 'timeline' ? 1 : 0,
        }}
        transition={panelCrossfade}
        style={{
          pointerEvents: wideScreen || mobileTab === 'timeline' ? 'auto' : 'none',
          zIndex: wideScreen ? undefined : mobileTab === 'timeline' ? 2 : 1,
        }}
      >
        <div
          ref={setTimelineScrollPort}
          className="scroll-region overscroll-y-contain min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-clip ps-4 pe-4 pt-4 pb-[max(5rem,env(safe-area-inset-bottom))] sm:ps-6 sm:pe-7 sm:pt-6 sm:pb-[max(5.5rem,env(safe-area-inset-bottom))] lg:pb-8 lg:pe-8"
        >
          <div
            className="will-change-transform"
            style={{
              transform: `translate3d(0, ${timelinePullPx}px, 0)`,
            }}
          >
            <div className="select-none" aria-live="polite">
              {timelineRefreshing ||
              timelinePullPx >= 8 ||
              timelinePullCommitFlying ||
              timelineIndicatorRefreshing ? (
                <TimelinePullIndicator
                  pullPx={
                    timelinePullCommitFlying
                      ? timelinePullReleaseThreshold
                      : !timelineHydrated && timelineRemoteLoading
                        ? timelinePullReleaseThreshold
                        : timelinePullPx
                  }
                  releaseThreshold={timelinePullReleaseThreshold}
                  angular={angular}
                  commitFly={timelinePullCommitFlying}
                  onCommitFlyComplete={onTimelinePullCommitFlyComplete}
                  refreshing={timelineIndicatorRefreshing}
                />
              ) : (
                <div className="h-0 min-h-px shrink-0" aria-hidden />
              )}
            </div>
          {!timelineHydrated ? null : displayRounds.length === 0 ? (
            <ThemeReveal>
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-surface-muted/85 py-12 text-center sm:min-h-[280px]">
              <p className="text-sm font-medium text-ink">还没有生成记录</p>
              <p className="mt-1 max-w-sm px-4 text-xs text-ink-soft">
                在左侧填好参数并点击「生成」后，结果会按轮次出现在时间线里。
              </p>
            </div>
            </ThemeReveal>
          ) : (
            <ThemeStagger
              className={[
                'mx-auto w-full max-w-5xl',
                '[--glyphicai-tl-gap:2rem]',
                '[--glyphicai-tl-stem-blend:min(7.5rem,45%)]',
                '[--glyphicai-tl-stem-blend-mid:min(4rem,22%)]',
              ].join(' ')}
            >
              <TimelineListShell
                roundIds={displayRounds.map((r) => r.id)}
                segmentStops={timelineSpineSegmentStops}
                angular={angular}
              >
              {displayRounds.map((round, roundIdx) => {
                const roundView = normalizeTimelineRound(round)
                const aspectStyle = timelineImageAspectStyle(roundView.params)
                const frameClampStyle = timelineFrameClampStyle(aspectStyle)
                const aspectMinH = timelineImageMinHeightClass(roundView.params)
                const status =
                  roundView.status ?? (roundView.items.length > 0 ? 'SUCCEEDED' : 'PENDING')
                const deferring = isDeferringTimelineRound(roundView)
                const awaitingGen = isRoundAwaitingGeneration(roundView)
                const accent = timelineRoundAccent(deferring, awaitingGen, status)
                // 追加生成发生在已有成功图的轮次上：整轮不算「忙」，
                // 只把 pendingItems 骨架接在已有图后面，避免原图被骨架屏盖掉。
                const hasSuccessfulItems = roundView.items.length > 0 && status === 'SUCCEEDED'
                const pendingSkeletonCount = roundView.pendingItems?.length ?? 0
                const railBusy = !hasSuccessfulItems && (deferring || awaitingGen)
                const timelineImageCornerCls = angular ? 'rounded-none' : 'rounded-2xl'
                const imageFrameClass = [
                  'relative isolate overflow-hidden border shadow-card w-full',
                  railBusy ? accent.frameBorder : 'border-line',
                  railBusy ? 'bg-surface-muted' : 'bg-transparent',
                  aspectMinH,
                  timelineImageCornerCls,
                ]
                  .filter(Boolean)
                  .join(' ')
                const metaItems = buildImageGenMetaFromTimelineRound({
                  ...roundView,
                  expectedImageCount: timelineExpectedImageCount(roundView),
                })
                const roundMetaLine = metaItems.join(' / ')
                const promptExpanded = activeRoundPromptId === round.id
                const showPromptToggle = round.prompt.length > PROMPT_COLLAPSE_THRESHOLD
                const promptText = promptExpanded ? round.prompt : collapsedTimelinePrompt(round)
                const displayItems: Array<GenItem | null> = hasSuccessfulItems
                  ? [
                      ...roundView.items,
                      ...Array.from({ length: pendingSkeletonCount }, () => null),
                    ]
                  : railBusy
                    ? Array.from({ length: timelineExpectedImageCount(roundView) }, () => null)
                    : roundView.items
                const showImageLoss = !railBusy && status === 'UNAVAILABLE'
                const imageLossFrameClass = [
                  'relative isolate flex w-full flex-col items-center justify-center gap-2 border border-dashed border-line bg-surface-muted/65 px-4 py-10 text-center shadow-card',
                  aspectMinH,
                  timelineImageCornerCls,
                ].join(' ')
                const roundAllInLibrary =
                  libraryCatalog != null &&
                  roundView.items.length > 0 &&
                  roundView.items.every((it) => libraryCatalog.genItemIds.has(it.id))
                const libraryRoundStat = countRoundItemsInLibrary(
                  roundView.items,
                  libraryCatalog?.genItemIds,
                )
                const isLastRound = roundIdx === displayRounds.length - 1
                return (
                <Fragment key={round.id}>
                <div
                  className="contents"
                  role="group"
                  aria-busy={railBusy ? 'true' : 'false'}
                  aria-label={`${roundView.at}，${accent.srStatus}`}
                >
                  <TimelineRoundRail
                    roundId={round.id}
                    marker={timelineRailMarkers[roundIdx]!}
                    angular={angular}
                    accent={accent}
                  >
                    <TimelineRoundStrip className="pb-1 pl-0 pr-1" trackClassName="gap-3">
                      {(stripScrollRoot) =>
                        showImageLoss ? (
                          <div className={TIMELINE_STRIP_CARD_WIDTH_CLASS}>
                            <div
                              className={imageLossFrameClass}
                              style={frameClampStyle}
                              role="status"
                              aria-label="该轮无可用图片"
                            >
                              <p className="text-sm font-medium text-ink">图片不可用</p>
                              <p className="max-w-[13.5rem] text-xs leading-relaxed text-ink-soft">
                                {roundView.errorMessage?.trim() ??
                                  '无有效图片地址，可能已失效或未保存。你可重新发起生成或检查存储配置。'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          displayItems.map((item, idx) => (
                            <div
                              key={item?.id ?? `${round.id}-skeleton-${idx}`}
                              className={TIMELINE_STRIP_CARD_WIDTH_CLASS}
                            >
                              {item ? (
                                <TimelineAspectImage
                                  src={item.src}
                                  className={imageFrameClass}
                                  cornerClass={timelineImageCornerCls}
                                  style={frameClampStyle}
                                  scrollRootEl={stripScrollRoot}
                                  deferSrc={idx > 0}
                                  angular={angular}
                                  downloadNameHint={`${(roundView.promptPreview ?? roundView.prompt).trim().slice(0, 32).replace(/[/\\?*:|"<>]/g, '_')}_${item.id.slice(0, 8)}`}
                                  libraryDot={
                                    libraryCatalog == null
                                      ? undefined
                                      : libraryCatalog.genItemIds.has(item.id)
                                        ? 'in'
                                        : 'out'
                                  }
                                  superResolutionOk={(() => {
                                    const sr = roundView.outputSuperResolution
                                    if (!sr?.attempted || !Array.isArray(sr.perImageSuccess)) return false
                                    return sr.perImageSuccess[idx] === true
                                  })()}
                                  onPreview={() =>
                                    openImageLightbox(
                                      roundView.items.map((it) => {
                                        const details: Array<{ label: string; value: string }> = []
                                        if (roundView.source === 'agent') details.push({ label: '来源', value: '智能体' })
                                        if (roundView.modelLabel) details.push({ label: '模型', value: roundView.modelLabel })
                                        if (roundView.aspectLabel) details.push({ label: '比例', value: roundView.aspectLabel })
                                        if (roundView.params?.size) details.push({ label: '尺寸', value: roundView.params.size })
                                        if (roundView.params?.quality) details.push({ label: '质量', value: roundView.params.quality })
                                        if (roundView.referenceImageCount && roundView.referenceImageCount > 0) {
                                          details.push({ label: '参考图', value: `${roundView.referenceImageCount} 张` })
                                        }
                                        if (roundView.at) details.push({ label: '时间', value: roundView.at })
                                        return {
                                          src: it.src,
                                          prompt: roundView.prompt,
                                          subtitle: roundMetaLine || undefined,
                                          taskLabel: roundView.taskId
                                            ? `任务 #${roundView.taskId}`
                                            : `轮次 ${roundView.id.slice(0, 8)}`,
                                          details,
                                          referenceImageUrls: roundView.referenceImages?.length ? roundView.referenceImages : undefined,
                                        }
                                      }),
                                      { index: idx },
                                    )
                                  }
                                />
                              ) : (
                                <div
                                  className={['glyphicai-timeline-skeleton', imageFrameClass].join(' ')}
                                  style={frameClampStyle}
                                  aria-busy="true"
                                  aria-label={deferring ? '等待发送生成请求' : '图片生成中'}
                                >
                                  {!deferring ? (
                                    <div className="pointer-events-none absolute left-3 top-3 z-[1] rounded-md border border-line bg-panel px-2 py-1 text-[11px] font-semibold text-ink shadow-card">
                                      生成中…
                                    </div>
                                  ) : null}
                                  {deferring && roundView.pendingSubmitDeadline ? (
                                    <TimelineDeferPlaceholder
                                      deadline={roundView.pendingSubmitDeadline}
                                      angular={angular}
                                      onCancel={() => {
                                        if (deferringRoundIdRef.current !== roundView.id) return
                                        generationDeferControllerRef.current?.abort()
                                      }}
                                    />
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                    </TimelineRoundStrip>
                  <div className="mt-4 max-w-3xl space-y-2">
                    <div className="space-y-1">
                      <p
                        className={[
                          'text-xs leading-snug text-ink sm:text-sm sm:leading-relaxed',
                          promptExpanded ? '' : 'line-clamp-2',
                        ].join(' ')}
                      >
                        {promptText}
                      </p>
                      {showPromptToggle ? (
                        <button
                          type="button"
                          onClick={() =>
                            setActiveRoundPromptId((prev) => (prev === round.id ? null : round.id))
                          }
                          className="text-xs font-medium text-accent hover:underline"
                        >
                          {promptExpanded ? '收起提示词' : '展开完整提示词'}
                        </button>
                      ) : null}
                    </div>
                    <div
                      className={[
                        'flex items-center gap-3 border bg-panel/75 px-2.5 py-2 text-[11px] text-ink/90 sm:px-3 sm:text-xs',
                        angular ? 'rounded-none' : 'rounded-lg',
                        accent.cardBorder,
                      ].join(' ')}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="sr-only">状态：{accent.srStatus}</span>
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                          {roundView.source === 'agent' ? (
                            <span
                              className={[
                                'shrink-0 border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[10px] font-semibold text-accent',
                                angular ? 'rounded-none' : 'rounded',
                              ].join(' ')}
                            >
                              智能体
                            </span>
                          ) : null}
                          {round.taskId ? (
                            <span className="max-w-full truncate font-mono text-ink" title={round.taskId}>
                              #{round.taskId}
                            </span>
                          ) : null}
                          {round.taskId &&
                          round.referenceImageCount != null &&
                          round.referenceImageCount > 0 ? (
                            <span aria-hidden className="text-ink/40">
                                  /
                                </span>
                          ) : null}
                          {round.referenceImageCount != null && round.referenceImageCount > 0 ? (
                            <button
                              type="button"
                              disabled={!round.referenceImages?.length}
                              onClick={() =>
                                openImageLightbox(
                                  (round.referenceImages ?? []).map((src, i) => ({
                                    src,
                                    taskLabel: round.taskId ? `任务 #${round.taskId}` : '参考图',
                                    subtitle: `参考图 ${i + 1}/${round.referenceImageCount}`,
                                    prompt: round.prompt,
                                  })),
                                  { index: 0 },
                                )
                              }
                              className="font-medium text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              改图参考 {round.referenceImageCount} 张
                            </button>
                          ) : null}
                          {(() => {
                            const chip = outputSuperResolutionTimelineChip(roundView.outputSuperResolution, angular)
                            if (!chip) return null
                            const showSlash =
                              Boolean(round.taskId) ||
                              (round.referenceImageCount != null && round.referenceImageCount > 0)
                            return (
                              <>
                                {showSlash ? (
                                  <span aria-hidden className="text-ink/40">
                                    /
                                  </span>
                                ) : null}
                                <span className={chip.className} title={preferHoverTitles ? chip.title : undefined}>
                                  {chip.label}
                                </span>
                              </>
                            )
                          })()}
                          {libraryCatalog != null &&
                          libraryRoundStat.total > 0 &&
                          status === 'SUCCEEDED' &&
                          !railBusy ? (
                            <>
                              {(Boolean(round.taskId) ||
                                (round.referenceImageCount != null && round.referenceImageCount > 0) ||
                                outputSuperResolutionTimelineChip(roundView.outputSuperResolution, angular) !=
                                  null) ? (
                                <span aria-hidden className="text-ink/40">
                                  /
                                </span>
                              ) : null}
                              <span
                                className={[
                                  chromeTimelineKpiChipShellClass(angular),
                                  'bg-[color-mix(in_srgb,var(--surface-muted)_52%,var(--panel)_48%)] text-ink/85',
                                ].join(' ')}
                                title={
                                  preferHoverTitles
                                    ? `本批共 ${libraryRoundStat.total} 张生成图，其中 ${libraryRoundStat.saved} 张已关联图库`
                                    : undefined
                                }
                              >
                                入库 {libraryRoundStat.saved}/{libraryRoundStat.total}
                              </span>
                            </>
                          ) : null}
                        </div>
                        <p className="mt-1 truncate text-ink/72">
                          {metaItems.map((meta, i) => (
                            <span key={`${round.id}-${i}-${meta}`}>
                              {i > 0 ? <span className="mx-1.5 text-ink/38">/</span> : null}
                              {meta}
                            </span>
                          ))}
                        </p>
                      </div>
                      {!railBusy && status === 'SUCCEEDED' ? (
                        <ChromeRoundActionMenu
                          angular={angular}
                          ariaLabel="轮次快捷操作"
                          actions={[
                            {
                              key: 'append',
                              tooltip: '使用相同提示词追加生成一张',
                              onSelect: () => void appendRoundGeneration(roundView.id),
                              disabled: !canSubmitCreation,
                            },
                            {
                              key: 'save',
                              tooltip: roundAllInLibrary
                                ? '本批图片均已在图库中'
                                : '存入个人图库',
                              onSelect: () => {
                                setSaveModalItems(roundView.items)
                                setSaveModalReferenceUrls(roundView.referenceImages?.length ? roundView.referenceImages : undefined)
                              },
                              disabled: roundView.items.length === 0 || roundAllInLibrary,
                            },
                            {
                              key: 'delete',
                              tooltip: '删除本条时间线的所有数据',
                              danger: true,
                              onSelect: () => setConfirmDeleteRoundId(roundView.id),
                            },
                          ]}
                        />
                      ) : null}
                    </div>
                    {status === 'FAILED' && roundView.errorMessage ? (
                      <div className="rounded-lg border border-[color-mix(in_srgb,var(--line)_72%,rgb(225_110_125)_28%)] bg-[color-mix(in_srgb,var(--panel)_88%,rgb(248_165_174)_12%)] px-3 py-2 text-xs font-medium leading-relaxed text-ink break-words [overflow-wrap:anywhere]">
                        失败原因：
                        {sanitizeUserFacingErrorMessage(roundView.errorMessage)}
                      </div>
                    ) : null}
                    {status === 'CANCELLED' ? (
                      <div className="rounded-lg border border-[color-mix(in_srgb,var(--line)_68%,var(--ink-soft)_32%)] bg-[color-mix(in_srgb,var(--panel)_90%,var(--ink-soft)_10%)] px-3 py-2 text-xs font-medium leading-relaxed text-ink">
                        {roundView.errorMessage?.trim() || '本趟生成未发送到服务器'}
                      </div>
                    ) : null}
                    {status === 'FAILED' && !railBusy ? (
                      <div className="flex flex-wrap gap-2 pt-0.5">
                        <ChromePrimaryButton
                          type="button"
                          density="compact"
                          onClick={() => {
                            setRetryRoundId(roundView.id)
                            setRetryPrompt(roundView.prompt)
                            setRetryModalOpen(true)
                          }}
                        >
                          重新生成
                        </ChromePrimaryButton>
                        <ChromeToolbarDangerButton
                          type="button"
                          onClick={() => {
                            removeRound(roundView.id)
                            if (activeRoundPromptId === roundView.id) setActiveRoundPromptId(null)
                          }}
                          title="从时间线删除此轮"
                        >
                          删除
                        </ChromeToolbarDangerButton>
                      </div>
                    ) : null}
                    {status === 'CANCELLED' && !railBusy ? (
                      <ChromeRoundActionMenu
                        angular={angular}
                        ariaLabel="轮次快捷操作"
                        actions={[
                          {
                            key: 'append',
                            tooltip: '本轮回生成未成功，无法追加',
                            disabled: true,
                          },
                          {
                            key: 'save',
                            tooltip: '本轮回无图片，无法存入图库',
                            disabled: true,
                          },
                          {
                            key: 'delete',
                            tooltip: '删除本条时间线的所有数据',
                            danger: true,
                            onSelect: () => {
                              removeRound(roundView.id)
                              if (activeRoundPromptId === roundView.id) setActiveRoundPromptId(null)
                            },
                          },
                        ]}
                      />
                    ) : null}
                    {!railBusy && status === 'UNAVAILABLE' ? (
                      <div className="flex flex-wrap gap-2 pt-0.5">
                        <ChromeToolbarDangerButton
                          type="button"
                          onClick={() => {
                            removeRound(roundView.id)
                            if (activeRoundPromptId === roundView.id) setActiveRoundPromptId(null)
                          }}
                          title="从时间线删除此轮"
                        >
                          删除
                        </ChromeToolbarDangerButton>
                      </div>
                    ) : null}
                    {/* 卡在前端的活骨架（有 taskId 的走后端取消接口，无 taskId 的直接 CANCELLED） */}
                    {railBusy && !roundView.taskId ? (
                      <div className="flex flex-wrap gap-2 pt-0.5">
                        <ChromePrimaryButton
                          type="button"
                          density="compact"
                          onClick={() => {
                            upsertRound({
                              ...roundView,
                              status: 'CANCELLED',
                              errorMessage: '用户丢弃',
                              pendingSubmitDeadline: undefined,
                            })
                          }}
                        >
                          丢弃
                        </ChromePrimaryButton>
                      </div>
                    ) : null}
                  </div>
                  </TimelineRoundRail>
                  {!isLastRound ? <TimelineRoundGapRow /> : null}
                </div>
                </Fragment>
                )
              })}
              </TimelineListShell>
            </ThemeStagger>
          )}
          </div>
        </div>
      </motion.section>
      </div>
    </div>
    </ContentSkeleton>
    </>
  )
}
